import { useTranslation } from 'react-i18next';
import { ServiceGroup } from '@turizi/shared';
import { Loader, Badge } from '@mantine/core';
import { SERVICE_GROUPS_PATH } from '@/lib/navigation';
import { useDeleteServiceGroup } from '@/hooks/api-hooks';
import { ServiceTitleStatic } from './services/service-title';
import { useNavigate } from 'react-router-dom';
import { getServiceGroupTotalPrice } from '@/lib/models-utils';
import { MoneyIn } from './snippets';
import { TagIcon } from '@/lib/tag-utils';

interface Props {
    serviceGroups: ServiceGroup[];
    isLoading: boolean;
}

export default function ServiceGroupsEditList({ serviceGroups, isLoading }: Props): JSX.Element {
    const { t } = useTranslation();
    const navigate = useNavigate();

    if (isLoading) {
        return <Loader />;
    }

    return (
        <div className="table-container">
            <div className="subtle-table">
                <table>
                    <thead>
                        <tr>
                            <th>ID</th>
                            <th>{t('name')}</th>
                            <th className="flex items-center justify-end">{t('price')}</th>
                            <th>{t('services')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {serviceGroups.map((serviceGroup) => (
                            <tr
                                key={serviceGroup.id}
                                onClick={() => navigate(`${SERVICE_GROUPS_PATH}/${serviceGroup.id}`)}
                                className="cursor-pointer hover:bg-gray-100"
                            >
                                <td>
                                    <div className="text-xs text-gray-400">
                                        #{serviceGroup.id}
                                    </div>
                                </td>
                                <td className="font-semibold">
                                    {serviceGroup.name}
                                    <div className="text-xxs text-gray-400 max-w-[350px] line-clamp-5 whitespace-pre-wrap">
                                        {serviceGroup.observations}
                                    </div>
                                </td>
                                <td>
                                    <MoneyIn {...getServiceGroupTotalPrice(serviceGroup)} />
                                </td>
                                <td>
                                    <div className="space-y-1">
                                        {serviceGroup.service_links?.sort((a, b) => a.service.category.localeCompare(b.service.category))?.map((link) => (
                                            <div
                                                key={link.service_id}
                                                className="flex items-center gap-2 bg-gray-50 px-2 py-0.5 rounded-full w-fit"
                                            >
                                                <div className={`text-xxs ${link.discount > 0 ? 'text-green-500' : 'text-gray-400'}`}>
                                                    {link.discount > 0 ? `-${link.discount}%` : '00.00%'}
                                                </div>
                                                <div className="text-xs font-medium flex gap-1 items-center">
                                                    <TagIcon name={link.service.category} hideText noBackground size="xxs" />
                                                    {ServiceTitleStatic(link.service)}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div >
    );
}
